import { ReactElement } from "react";
import { resolveProperty } from "@animech/axiom-utility";
import {
	FieldComponentBuilderArgs,
	FieldContainer,
	FieldPipe,
	FieldPipeMeta,
	UiComponentBuildContext,
	UiComponentBuilder,
} from "@animech/axiom-ui-builder";
import { getDeviceAttributes, Grid } from "@animech/ui";
import { calculateGridItemSize } from "@animech/axiom-ui-builder/src/utility";
import { Root } from "@definitions";
import { ColorSelectWithColorPropagationNode } from "src/definitions/ui-definitions";
import { ColorSelectWithColorPropagationField } from "src/ui/components/widgets";

type ComponentBuilderArgs = FieldComponentBuilderArgs<
	Root,
	ColorSelectWithColorPropagationNode
>;

export class ColorSelectWithColorPropagationComponentBuilder
	implements UiComponentBuilder<Root, ColorSelectWithColorPropagationNode>
{
	private _args: ComponentBuilderArgs;

	constructor(args: ComponentBuilderArgs) {
		this._args = { ...args };
	}

	public build(
		node: ColorSelectWithColorPropagationNode,
		context: UiComponentBuildContext<Root>,
	) {
		const { getFieldNodeMeta, getFieldNodeValueMeta } = this._args;

		if (node.state) {
			const property = resolveProperty(node.state, context.activeProperty);
			return (
				<FieldPipe
					key={`${node.id}:${context.activeProperty}`}
					property={property}
					write
				>
					<FieldPipeMeta
						getFieldMeta={(property) => getFieldNodeMeta(node, property)}
						getFieldValueMeta={getFieldNodeValueMeta}
					>
						{this._render(node, context)}
					</FieldPipeMeta>
				</FieldPipe>
			);
		} else {
			return this._render(node, context);
		}
	}

	private _render(
		node: ColorSelectWithColorPropagationNode,
		context: UiComponentBuildContext<Root>,
	): ReactElement {
		const { showLabel, showShortInfo } = node;
		const { activeProperty: property } = context;

		const fieldProps = {
			property,
		};

		const fieldContainerProps = {
			property,
			showFieldLabel: showLabel,
			showFieldInfo: showShortInfo,
			fieldLabelProps: {
				size: "labelSize" in node ? node.labelSize : "default",
			},
		};

		const container = node.container;
		if (container?.type === "grid") {
			const gridSizeAttributes = container.itemSize
				? getDeviceAttributes(
						calculateGridItemSize(container.itemSize),
						"data-size",
					)
				: {};
			return (
				<FieldContainer key={node.id} {...fieldProps} {...fieldContainerProps}>
					<Grid
						align={container.align}
						spacing={container.spacing}
						itemWidth={container.itemWidth}
						{...gridSizeAttributes}
					>
						<ColorSelectWithColorPropagationField />
					</Grid>
				</FieldContainer>
			);
		}

		return (
			<FieldContainer key={node.id} {...fieldProps} {...fieldContainerProps}>
				<ColorSelectWithColorPropagationField />
			</FieldContainer>
		);
	}
}
